import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./NegotiationBot.module.css";
import {
  getChatTime,
  getDecisionClassKey,
  getMessageText,
  getMessageTimestamp,
  isBuyerMessage,
  getSessionStatusText,
  normalizeMoneyText,
} from "./negotiationUtils";

const NegotiationChat = ({
  sessions,
  selectedSessionToken,
  sessionDetail,
  onSelectSession,
  onRefreshSession,
  onSendMessage,
  loading,
}) => {
  const [message, setMessage] = useState("");
  const [localMessages, setLocalMessages] = useState([]);
  const streamRef = useRef(null);
  const rounds = Array.isArray(sessionDetail?.rounds) ? sessionDetail.rounds : [];
  const activeSession =
    sessions.find(
      (session) => session.negotiation_session_token === selectedSessionToken,
    ) || {};
  const statusText = getSessionStatusText(sessionDetail || activeSession);
  const persistedMessages = useMemo(
    () => (Array.isArray(sessionDetail?.messages) ? sessionDetail.messages : []),
    [sessionDetail?.messages],
  );
  const visibleMessages = useMemo(
    () => persistedMessages.filter((item) => getMessageText(item).trim()),
    [persistedMessages],
  );
  const firstBuyerMessageIndex = visibleMessages.findIndex((item) =>
    isBuyerMessage(item),
  );

  useEffect(() => {
    streamRef.current?.scrollTo({
      top: streamRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [rounds.length, localMessages.length, persistedMessages.length, loading]);

  const sendBuyerMessage = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;

    const pendingMessage = {
      id: `${Date.now()}`,
      text: trimmedMessage,
      createdAt: new Date().toISOString(),
    };
    setLocalMessages((prev) => [...prev, pendingMessage]);
    setMessage("");

    await onSendMessage?.({
      negotiationSessionToken: selectedSessionToken,
      message: trimmedMessage,
    });
  };

  useEffect(() => {
    if (!localMessages.length || !persistedMessages.length) return;

    setLocalMessages((prev) =>
      prev.filter(
        (localMessage) =>
          !persistedMessages.some(
            (persistedMessage) =>
              isBuyerMessage(persistedMessage) &&
              getMessageText(persistedMessage).trim() === localMessage.text.trim(),
          ),
      ),
    );
  }, [localMessages.length, persistedMessages]);

  const renderRoundUpdates = () =>
    rounds.map((round, index) => {
      const previousOffer =
        index === 0 ? sessionDetail?.initial_price : rounds[index - 1]?.offered_price;
      const roundMessage = String(round.vendor_message || "").trim();
      const hasPersistedRoundMessage = visibleMessages.some(
        (item) =>
          !isBuyerMessage(item) &&
          getMessageText(item).trim() === roundMessage,
      );

      return (
        <React.Fragment key={round.negotiation_round_token || round.round}>
          <div className={styles.priceChangeEvent}>
            <span className={styles.priceChangeLabel}>
              Revised offer submitted
            </span>
            <span className={styles.priceChangeValues}>
              <span className={styles.priceChangeOld}>
                {normalizeMoneyText(previousOffer, round.currency)}
              </span>
              <span className={styles.priceChangeArrow} aria-hidden="true">
                -&gt;
              </span>
              <span className={styles.priceChangeNew}>
                {normalizeMoneyText(round.offered_price, round.currency)}
              </span>
            </span>
          </div>

          {roundMessage && !hasPersistedRoundMessage ? (
            <div className={styles.messageRowLeft}>
              <div className={styles.avatarDot}>V</div>
              <div className={styles.messageStack}>
                <span className={styles.senderLabel}>Vendor</span>
                <div className={styles.messageBubbleLeft}>{roundMessage}</div>
                <small>{getChatTime(round.created_at)}</small>
              </div>
            </div>
          ) : null}
        </React.Fragment>
      );
    });

  return (
    <section className={styles.chatPanel}>
      <div className={styles.sessionRail}>
        {sessions.map((session) => (
          <button
            type="button"
            key={session.negotiation_session_token}
            className={
              selectedSessionToken === session.negotiation_session_token
                ? styles.sessionActive
                : ""
            }
            onClick={() => onSelectSession(session.negotiation_session_token)}
          >
            <span title={session.vendor_name}>{session.vendor_name || "-"}</span>
            <small>
              Round {session.current_round ?? 0}/{session.max_rounds ?? "-"}
            </small>
          </button>
        ))}
      </div>

      {selectedSessionToken ? (
        <>
          <div className={styles.chatStatusHeader}>
            <div>
              <h4>{sessionDetail?.vendor_name || activeSession.vendor_name || "Vendor"}</h4>
              <p>{sessionDetail?.rfq_code || "RFQ negotiation"}</p>
            </div>
            <span
              className={`${styles.liveStatus} ${
                styles[getDecisionClassKey(sessionDetail?.decision || activeSession.decision)]
              }`}
            >
              <i></i>
              {statusText}
            </span>
          </div>

          <div className={styles.chatConversation} ref={streamRef}>
            {firstBuyerMessageIndex === -1 ? renderRoundUpdates() : null}

            {visibleMessages.map((item, index) => {
              const text = getMessageText(item);
              const buyerMessage = isBuyerMessage(item);

              return (
                <React.Fragment
                  key={item.message_token || item.token || item.id || index}
                >
                  <div
                    className={
                      buyerMessage ? styles.messageRowRight : styles.messageRowLeft
                    }
                  >
                    {!buyerMessage ? <div className={styles.avatarDot}>V</div> : null}
                    <div className={styles.messageStack}>
                      <span className={styles.senderLabel}>
                        {buyerMessage ? "You" : "Vendor"}
                      </span>
                      <div
                        className={
                          buyerMessage
                            ? styles.messageBubbleRight
                            : styles.messageBubbleLeft
                        }
                      >
                        {text}
                      </div>
                      <small>{getChatTime(getMessageTimestamp(item))}</small>
                    </div>
                  </div>
                  {index === firstBuyerMessageIndex ? renderRoundUpdates() : null}
                </React.Fragment>
              );
            })}

            {localMessages.map((item) => (
              <div className={styles.messageRowRight} key={item.id}>
                <div className={styles.messageStack}>
                  <span className={styles.senderLabel}>You</span>
                  <div className={styles.messageBubbleRight}>{item.text}</div>
                  <small>{getChatTime(item.createdAt)}</small>
                </div>
              </div>
            ))}

            {loading ? (
              <div className={styles.typingIndicator}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            ) : null}

            {!loading && rounds.length === 0 && visibleMessages.length === 0 ? (
              <div className={styles.emptyState}>No vendor reply received yet.</div>
            ) : null}
          </div>

          <div className={styles.chatComposer}>
            <input
              value={message}
              placeholder="Type message..."
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") sendBuyerMessage();
              }}
            />
            <button type="button" disabled={loading} onClick={sendBuyerMessage}>
              Send
            </button>
            <button
              type="button"
              className={styles.iconRefresh}
              disabled={loading}
              onClick={() => onRefreshSession(selectedSessionToken)}
            >
              Refresh
            </button>
          </div>
        </>
      ) : (
        <div className={styles.emptyState}>
          No negotiation chat started yet. Use Analysis to select vendors and send
          a revision request.
        </div>
      )}
    </section>
  );
};

export default NegotiationChat;
